function makePriceValue(price) {
  if (price == null) {
    return null;
  }

  if (typeof price === 'object' && price.value != null) {
    return Number(price.value);
  }

  return Number(price);
}

export function createPriceSummary(dormsFrom, privatesFrom) {
  // Plain data only — methods on Pinia state are stripped during SSR hydration.
  return {
    dormsFrom,
    privatesFrom,
  };
}

/**
 * @param {{ dormsFrom?: number|null, privatesFrom?: number|null }|null|undefined} prices
 * @returns {number|null} Lower of dormsFrom and privatesFrom, or the only set value, or null.
 */
export function getLowestAveragePrice(prices) {
  if (!prices) {
    return null;
  }

  const {
    dormsFrom,
    privatesFrom,
  } = prices;

  if (dormsFrom && privatesFrom) {
    return Math.min(dormsFrom, privatesFrom);
  }

  return dormsFrom || privatesFrom || null;
}

/**
 * @param {{ dormsFrom?: number|null, privatesFrom?: number|null }|null|undefined} prices
 * @returns {number} Higher of dormsFrom and privatesFrom, the only set value, or 0.
 */
export function getHighestAveragePrice(prices) {
  if (!prices) {
    return 0;
  }

  const {
    dormsFrom,
    privatesFrom,
  } = prices;

  if (dormsFrom && privatesFrom) {
    return Math.max(dormsFrom, privatesFrom);
  }

  return privatesFrom || dormsFrom || 0;
}

function makeRatingTotal(overallRating) {
  const total = Number(overallRating?.overall);
  if (!total || total <= 0 || total > 100) {
    return 0;
  }

  return Number((total / 10).toFixed(1));
}

function makeFacilities(categories) {
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories.flatMap((category) => (category?.facilities || []).map((facility) => facility.id)).filter(Boolean);
}

function makeRooms(breakdown) {
  if (!breakdown) {
    return [];
  }

  const mapRoom = (room) => ({
    basicType: room.basicType,
    ensuite: Boolean(room.ensuite),
    capacity: Number(room.capacity),
  });

  return [...(breakdown.dorms || []).map(mapRoom), ...(breakdown.privates || []).map(mapRoom)];
}

function makePromotionsDetails(lowestAverageDorm, lowestAveragePrivate) {
  const hasPromotions = Boolean(lowestAverageDorm?.promotions) || Boolean(lowestAveragePrivate?.promotions);
  const totalDiscountDorms = Number(lowestAverageDorm?.promotions?.totalDiscount || 0);
  const totalDiscountPrivates = Number(lowestAveragePrivate?.promotions?.totalDiscount || 0);
  const totalDiscount = hasPromotions ? Math.max(totalDiscountDorms, totalDiscountPrivates) : 0;
  const originalPrices = hasPromotions
    ? createPriceSummary(
      makePriceValue(lowestAverageDorm?.original),
      makePriceValue(lowestAveragePrivate?.original),
    )
    : null;

  return {
    hasPromotions,
    totalDiscount,
    totalDiscountDorms,
    totalDiscountPrivates,
    originalPrices,
  };
}

/**
 * Map a HW search API property payload to the SRP inventory shape.
 *
 * @param {object} data - Raw property from HW search API
 * @returns {object} Normalised search property
 */
export function mapSearchApiProperty(data) {
  const dormsFrom = makePriceValue(data.lowestAverageDormPricePerNight);
  const privatesFrom = makePriceValue(data.lowestAveragePrivatePricePerNight);
  const {
    hasPromotions,
    totalDiscount,
    totalDiscountDorms,
    totalDiscountPrivates,
    originalPrices,
  } = makePromotionsDetails(data.lowestAverageDormPricePerNight, data.lowestAveragePrivatePricePerNight);

  return {
    id: data.id,
    name: data.name,
    type: data.type,
    isFeatured: data.isFeatured,
    isNew: data.isNew,
    veryPopular: data.veryPopular,
    hpaProperty: false,
    hasFreeCancellation: Boolean(data.freeCancellationAvailable),
    latitude: data.latitude,
    longitude: data.longitude,
    distance: data.distance?.value ?? null,
    cityName: data?.city?.name || data?.cityName,
    prices: createPriceSummary(dormsFrom, privatesFrom),
    rating: {
      total: makeRatingTotal(data.overallRating),
      totalReviews: Number(data.overallRating?.numberOfRatings) || 0,
    },
    overallRating: data.overallRating,
    ratingBreakdown: data.ratingBreakdown,
    facilities: makeFacilities(data.facilities),
    districts: data.districts || [],
    rooms: makeRooms(data.rooms),
    imagesGallery: data.imagesGallery,
    promotions: data.promotions || [],
    categories: data.categories || [],
    totalDiscount,
    totalDiscountDorms,
    totalDiscountPrivates,
    hasPromotions,
    originalPrices,
  };
}

/**
 * Map microsite API property data for HPA/PPC pin fallback when property is missing from search results.
 *
 * @param {object} msApiProperty - Raw microsite property payload
 * @returns {object} Normalised search property with empty prices
 */
export function mapMicrositePropertyToSearchProperty(msApiProperty) {
  return {
    id: msApiProperty.id,
    name: msApiProperty.name,
    type: msApiProperty.type,
    isFeatured: false,
    isNew: msApiProperty.isNew,
    veryPopular: false,
    hpaProperty: true,
    latitude: msApiProperty?.propertyLocation?.latitudeFloat,
    longitude: msApiProperty?.propertyLocation?.longitudeFloat,
    cityName: msApiProperty?.location?.city,
    prices: createPriceSummary(null, null),
    rating: {
      total: makeRatingTotal(msApiProperty?.ratings || msApiProperty?.rating),
      totalReviews: Number((msApiProperty?.ratings || msApiProperty?.rating)?.numberOfRatings) || 0,
    },
    overallRating: msApiProperty?.ratings || msApiProperty?.rating,
    ratingBreakdown: msApiProperty?.ratingBreakdown,
    facilities: makeFacilities(msApiProperty?.facilities),
    districts: [],
    rooms: [],
    imagesGallery: msApiProperty?.images,
    promotions: msApiProperty?.promotions || [],
    categories: msApiProperty?.categories || [],
    totalDiscount: 0,
    distance: msApiProperty?.propertyLocation?.distance?.kilometers ?? null,
  };
}
